import type { IShellBootReportMessage, IShellBootTiming } from '@featit/shared';
import { postShellMessage } from './shellBridge';
import { hasShellCapability } from './shellDetection';

/**
 * T-0559: a cold launch's timings for the device log, where a headless simulator run has no Web
 * Inspector to attach.
 *
 * Sent after `ready`, and again when a launch held for its content has it (`sendSettledBootReportOnce`), once each per document, and only to a shell that lists `bootReport`, which a Debug
 * build of the app does and a Release or TestFlight build never does. It carries the
 * `featit:boot:*` marks and, for the document and the session check, Resource Timing's connection,
 * request and response times. A request is named, never addressed: no URL, query, header or token
 * leaves the page in it.
 */
const SESSION_PATHS: Record<string, IShellBootTiming['name']> = {
  '/api/auth/me': 'auth/me',
  '/api/auth/refresh': 'auth/refresh',
};

let sent = false;
let settledSent = false;

interface IServerTiming {
  app: number;
  db?: number;
}

/** The session check's own `Server-Timing`, from the latest answer before the report went. */
let sessionServerTiming: IServerTiming | null = null;

/** `app` and `db` from a `Server-Timing` header, in ms; any other metric in it is ignored. */
export function parseServerTiming(header: unknown): IServerTiming | null {
  if (typeof header !== 'string') return null;
  const durations: Record<string, number> = {};
  for (const metric of header.split(',')) {
    const [name, ...params] = metric.split(';').map((part) => part.trim());
    const duration = params.find((param) => param.startsWith('dur='));
    const value = duration === undefined ? Number.NaN : Number(duration.slice('dur='.length));
    if (name && Number.isFinite(value)) durations[name] = value;
  }
  if (durations.app === undefined) return null;
  return durations.db === undefined
    ? { app: durations.app }
    : { app: durations.app, db: durations.db };
}

/**
 * T-0559: called with each `/api/auth/me` answer's `Server-Timing` (`AuthApi.me`). WebKit in the app
 * gives the request's Resource Timing no connection or server detail, so the report splits the fetch
 * with what the server says it spent.
 */
export function noteSessionServerTiming(header: unknown): void {
  if (sent) return;
  const timing = parseServerTiming(header);
  if (timing) sessionServerTiming = timing;
}

function timingOf(
  name: IShellBootTiming['name'],
  entry: PerformanceResourceTiming,
): IShellBootTiming {
  const round = (value: number) => Math.round(value);
  return {
    name,
    startTime: round(entry.startTime),
    connectStart: round(entry.connectStart),
    connectEnd: round(entry.connectEnd),
    secureConnectionStart: round(entry.secureConnectionStart),
    requestStart: round(entry.requestStart),
    responseStart: round(entry.responseStart),
    responseEnd: round(entry.responseEnd),
    nextHopProtocol: entry.nextHopProtocol,
    transferSize: entry.transferSize,
  };
}

/** Paint Timing's two entries, by the name each carries; anything else in the list is ignored. */
const PAINTS = ['first-paint', 'first-contentful-paint'] as const;

/** I-1478: the most distinct read names one report carries; the rest are counted under `other`. */
export const MAX_READ_NAMES = 24;

/**
 * I-1478: an `/api/` path as a read's name: a segment with a digit in it (an id, a key such as
 * `I-1478`, a date) or a long one (a token, a slug) is `:id`, so a name is never one record's.
 */
export function readName(path: string): string {
  return path
    .split('/')
    .map((segment) => (/\d/.test(segment) || segment.length > 24 ? ':id' : segment))
    .join('/');
}

function countRead(reads: Record<string, number>, name: string): void {
  const key = name in reads || Object.keys(reads).length < MAX_READ_NAMES ? name : 'other';
  reads[key] = (reads[key] ?? 0) + 1;
}

/**
 * RD-32a: the Resource Timing entries a launch keeps. A browser keeps 250 unless told otherwise, and a
 * dev server's module requests alone fill that before the first read, after which every `/api/`
 * read and the session check's timings are dropped and the report says the launch read nothing.
 */
export const BOOT_RESOURCE_BUFFER = 5000;

/**
 * RD-32a: raises the buffer before the launch's first read (`index.tsx`, beside the entry mark), for a
 * shell that asks for the report; any other page keeps the browser's default.
 */
export function keepBootReadsInResourceTiming(perf: Performance = performance): void {
  if (!hasShellCapability('bootReport') || typeof perf.setResourceTimingBufferSize !== 'function')
    return;
  try {
    perf.setResourceTimingBufferSize(BOOT_RESOURCE_BUFFER);
  } catch {
    // A buffer that cannot be raised is a shorter log line, never a broken launch.
  }
}

/** The queries the launch made before `ready`, by operation name (`graphql:WorkItemsPage`). */
const graphqlReads: Record<string, number> = {};

/**
 * I-1478: called with each GraphQL query's document (`GraphqlClient.request`). Resource Timing sees
 * only `/api/graphql`, so the operation is what says which read it was. Counted until the launch's
 * content is settled (a report built at `ready` has only those so far).
 */
export function noteGraphqlRead(operation: string | null): void {
  if (settledSent || performance.getEntriesByName('featit:boot:content-settled', 'mark').length > 0)
    return;
  countRead(graphqlReads, `graphql:${operation ?? 'anonymous'}`);
}

/**
 * I-1381: what the browser alone knows, as marks beside the web's own, so a device log carries
 * what Safari's Web Inspector shows: the first paint, and the launch tab's data from its first
 * `/api/` request out to the last one answered by `ready` (the session check is timed on its own).
 */
function browserMarks(
  perf: Performance,
  ready: number | undefined,
  reads: Record<string, number>,
): Record<string, number> {
  const marks: Record<string, number> = {};
  for (const paint of perf.getEntriesByType('paint')) {
    if ((PAINTS as readonly string[]).includes(paint.name)) {
      marks[paint.name] = Math.round(paint.startTime);
    }
  }
  let firstDataStart: number | undefined;
  let lastDataEnd: number | undefined;
  for (const entry of perf.getEntriesByType('resource') as PerformanceResourceTiming[]) {
    let path: string;
    try {
      path = new URL(entry.name).pathname;
    } catch {
      continue;
    }
    if (!path.startsWith('/api/') || SESSION_PATHS[path]) continue;
    if (ready !== undefined && entry.startTime > ready) continue;
    // `/api/graphql` carries every query, so its reads are named by operation (`noteGraphqlRead`).
    if (path !== '/api/graphql') countRead(reads, readName(path));
    firstDataStart = Math.min(firstDataStart ?? entry.startTime, entry.startTime);
    lastDataEnd = Math.max(lastDataEnd ?? entry.responseEnd, entry.responseEnd);
  }
  if (firstDataStart !== undefined) marks['first-data-start'] = Math.round(firstDataStart);
  if (lastDataEnd !== undefined) marks['last-data-end'] = Math.round(lastDataEnd);
  return marks;
}

/** The report, read from the page's own timing. Exported for its test. */
export function buildBootReport(perf: Performance = performance): IShellBootReportMessage {
  const marks: Record<string, number> = {};
  for (const mark of perf.getEntriesByType('mark')) {
    if (mark.name.startsWith('featit:boot:')) {
      marks[mark.name.slice('featit:boot:'.length)] = Math.round(mark.startTime);
    }
  }
  const reads: Record<string, number> = {};
  Object.assign(marks, browserMarks(perf, marks['content-settled'] ?? marks.ready, reads));
  for (const [name, count] of Object.entries(graphqlReads)) {
    for (let counted = 0; counted < count; counted += 1) countRead(reads, name);
  }
  const timings: IShellBootTiming[] = [];
  const navigation = perf.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  if (navigation) timings.push(timingOf('document', navigation));
  let lastSessionCheck: { index: number; entry: PerformanceResourceTiming } | null = null;
  for (const entry of perf.getEntriesByType('resource') as PerformanceResourceTiming[]) {
    let path: string;
    try {
      path = new URL(entry.name).pathname;
    } catch {
      continue;
    }
    const name = SESSION_PATHS[path];
    if (!name) continue;
    if (name === 'auth/me') lastSessionCheck = { index: timings.length, entry };
    timings.push(timingOf(name, entry));
  }
  // The header came with the latest answer, so it splits the latest session check.
  if (lastSessionCheck && sessionServerTiming) {
    const { index, entry } = lastSessionCheck;
    const { app, db } = sessionServerTiming;
    timings[index] = {
      ...timings[index],
      server: Math.round(app),
      ...(db === undefined ? {} : { db: Math.round(db) }),
      transit: Math.round(entry.responseEnd - entry.startTime - app),
    };
  }
  return { type: 'bootReport', marks, timings, reads };
}

/** Posts the report once, to a shell that asked for it; anything else sends nothing. */
export function sendBootReportOnce(): void {
  if (sent || !hasShellCapability('bootReport')) return;
  sent = true;
  try {
    postShellMessage(buildBootReport());
  } catch {
    // A report that cannot be read is a missing log line, never a broken launch.
  }
}

/**
 * I-1478 (RD-32): a launch held for its content sends its report a second time, when the content is
 * in, so the reads between `ready` and the mark's leaving are counted too. Its marks carry
 * `content-settled`. The shell logs it as another report; a spare keeps the latest.
 */
export function sendSettledBootReportOnce(): void {
  if (settledSent || !hasShellCapability('bootReport')) return;
  settledSent = true;
  try {
    postShellMessage(buildBootReport());
  } catch {
    // As above: a missing log line, never a broken launch.
  }
}

export function resetBootReportForTests(): void {
  sent = false;
  settledSent = false;
  sessionServerTiming = null;
  for (const name of Object.keys(graphqlReads)) delete graphqlReads[name];
}
